import React from "react";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemText,
  ListItemButton,
  Paper,
  Box,
  Typography,
  Tooltip,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import { FormResponse, FormView } from "@/types/form";
import { useCallback, useState } from "react";
import { Edit as EditIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { downloadCSV } from "@/scripts/downloadCsv";

interface ProcessItemProps {
  formResponse: FormResponse;
  views: FormView[];
  deleteFormResponse: (formResponseId: string) => void;
}

const ProcessItem: React.FC<ProcessItemProps> = ({ formResponse, views, deleteFormResponse }) => {
  const navigate = useNavigate();
  const formResponseId = formResponse.id;
  const processTitle = formResponse.formDefinition.titleFieldId
    ? formResponse.responses.find((answer) => answer.id === formResponse.formDefinition.titleFieldId)
      ?.value
    : "Untitled Process";
  const [isSectionOptionOpened, setIsSectionOptionOpened] = useState(false);
  const [isViewOptionOpened, setIsViewOptionOpened] = useState(false);
  const [isDeleteItemOpened, setIsDeleteItemOpened] = useState(false);
  const [deleteDialogConfirmationValue, setDeleteDialogConfirmationValue] = useState("");
  const startSections = formResponse.formDefinition.sections.filter(
    (section) => section.isStartingSection
  );

  const handleStartFilling = useCallback(() => {
    if (startSections.length === 1) {
      navigate(
        `/form-filling?formResponseId=${formResponseId}&startingSection=${startSections[0].id}`
      ); // Navigate to form-filling page with formResponseId
      return;
    }

    setIsSectionOptionOpened(true);
  }, [navigate, formResponseId, startSections]);

  const selectSection = (sectionId: string) => {
    navigate(
      `/form-filling?formResponseId=${formResponseId}&startingSection=${sectionId}`
    ); // Navigate to view-process page with formResponseId
  };

  const editProcess = () => {
    navigate(`/form-authoring?formResponseId=${formResponseId}`);
  };

  const selectView = (viewId: string) => {
    navigate(`/view-form?viewId=${viewId}&formResponseId=${formResponseId}`);
  };

  const download = () => {
    const questions = formResponse.formDefinition.sections.flatMap(
      (section) => section.fields
    );
    const responses = formResponse.responses;

    const data = [
      Object.fromEntries(
        questions.map((question) => [
          question.label,
          responses.find((answer) => answer.id === question.id)?.value,
        ])
      ),
    ];

    downloadCSV(data, processTitle!);
  };

  const openDeleteItemDialog = () => {
    setIsDeleteItemOpened(true);
  }

  const isDeleteDisabled = deleteDialogConfirmationValue !== (processTitle ?? "");

  return (
    <Paper
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 2,
        mb: 2,
      }}
    >
      <Tooltip title={processTitle}>
        <Typography variant="body1" overflow="hidden" textOverflow="ellipsis">
          {processTitle}
        </Typography>
      </Tooltip>
      <Box sx={{ display: "flex", gap: 1 }}>
        <Tooltip title="Fill Form">
          <IconButton color="primary" onClick={handleStartFilling}>
            <PlayCircleOutlineIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit Form Questions">
          <IconButton color="primary" onClick={editProcess}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="View">
          <IconButton
            color="primary"
            onClick={() => setIsViewOptionOpened(true)}
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download">
          <IconButton color="primary" onClick={download}>
            <FileDownloadIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton color="error" onClick={openDeleteItemDialog}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
      { /* Delete confirmation dialog */}
      { /* Asks the user to write the title of the form that he is trying to delete, to confirm before deleting */}
      <Dialog
        open={isDeleteItemOpened}
        onClose={() => setIsDeleteItemOpened(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle>Delete Item</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this item?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Type the title of this item to confirm:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {processTitle}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Process Title"
            type="text"
            fullWidth
            variant="standard"
            value={deleteDialogConfirmationValue}
            onChange={(e) => setDeleteDialogConfirmationValue(e.target.value)}
            error={isDeleteDisabled}
            helperText={
              isDeleteDisabled
                ? "Process title does not match"
                : ""
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteItemOpened(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              deleteFormResponse(formResponseId);
              setIsDeleteItemOpened(false);
            }}
            startIcon={<DeleteIcon />}
            disabled={isDeleteDisabled}
            color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* Select Starting Section for filling */}
      <Dialog
        open={isSectionOptionOpened}
        onClose={() => setIsSectionOptionOpened(false)}
        aria-labelledby="section-dialog-title"
      >
        <DialogTitle>Select a starting section</DialogTitle>
        <DialogContent>
          <List>
            {startSections.map((section) => (
              <ListItemButton
                key={section.id}
                onClick={() => selectSection(section.id)}
              >
                <ListItemText primary={section.title} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>
      {/* Select View */}
      <Dialog
        open={isViewOptionOpened}
        onClose={() => setIsViewOptionOpened(false)}
        aria-labelledby="section-dialog-title"
      >
        <DialogTitle>Select a view</DialogTitle>
        <DialogContent>
          <List>
            {views.map((view) => (
              <ListItemButton key={view.id} onClick={() => selectView(view.id)}>
                <ListItemText primary={view.title} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default ProcessItem;
